import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaClock } from "react-icons/fa";
import { BiBookReader } from "react-icons/bi";
import "../CaseResult/Case.css";
import Go from "../Go/Go";
import Entry from "../Entry/Entry";

gsap.registerPlugin(ScrollTrigger);

const caseStudies = [
  {
    id: 1,
    title: "Family Violence Protection",
    description:
      "Successfully secured a protection order for a domestic violence survivor and her children. Our team provided comprehensive legal support, ensuring the client's safety and helping establish new living arrangements.",
    image: "/fvp.webp",
    tag: "Family Law",
    duration: "3 months",
    outcome: "Favorable verdict with full protection order granted",
  },
  {
    id: 2,
    title: "Giving Million Air Its Wings",
    description:
      "Defended aviation startup Million Air against corporate takeover, protecting their intellectual property and securing their market position. The case set important precedents in startup protection law.",
    image: "/pip.webp",
    tag: "Corporate",
    duration: "8 months",
    outcome: "Successful defense with $2.5M settlement",
  },
  {
    id: 3,
    title: "Child Custody Protection",
    description:
      "Won a complex custody battle for a father whose ex-spouse was attempting to relocate internationally with their child. Established important precedents regarding parental rights in relocation cases.",
    image: "/ccp.webp",
    tag: "Family Law",
    duration: "6 months",
    outcome: "Primary custody awarded to our client",
  },
  {
    id: 4,
    title: "Million Air Expansion Case",
    description:
      "Navigated complex international regulations to facilitate Million Air's expansion into European markets. Our legal framework ensured compliance across multiple jurisdictions while protecting the company's interests.",
    image: "/ilp.webp",
    tag: "International",
    duration: "5 months",
    outcome: "Successful market entry with 0 regulatory violations",
  },
  {
    id: 5,
    title: "Corporate Fraud Defense",
    description:
      "Represented a senior executive falsely accused of financial misconduct within a multinational corporation. We meticulously discredited the allegations, preserving the client's career and reputation.",
    image: "/cfd.webp",
    tag: "Criminal Defense",
    duration: "4 months",
    outcome: "All charges dropped, full professional reinstatement",
  },
];

const Case = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const descRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* ── Section heading reveal ── */
      gsap.fromTo(
        headingRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: { trigger: headingRef.current, start: "top 85%" },
        },
      );

      gsap.fromTo(
        descRef.current,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          delay: 0.15,
          ease: "power3.out",
          scrollTrigger: { trigger: descRef.current, start: "top 85%" },
        },
      );

      /* ── Cards stagger ── */
      gsap.utils.toArray(".cs-card").forEach((card, i) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: card, start: "top 88%" },
            delay: i * 0.08,
          },
        );

        /* image parallax inside card */
        const img = card.querySelector(".cs-card__img");
        if (img) {
          gsap.to(img, {
            yPercent: -10,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          });
        }
      });

      /* ── Faded watermark counter ── */
      gsap.fromTo(
        ".cs-watermark",
        { opacity: 0, scale: 0.92 },
        {
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: { trigger: ".cs-watermark", start: "top 90%" },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <Entry />

      <section ref={sectionRef} className="cs-section">
        {/* ── Section header ── */}
        <div className="cs-header">
          <div className="cs-eyebrow">
            <span className="cs-eyebrow-dot" />
            <span className="cs-eyebrow-text">Legal Victories</span>
          </div>

          <div className="cs-watermark" aria-hidden="true">
            CASE STUDIES
          </div>

          <h2 ref={headingRef} className="cs-heading">
            Our Most <em>Significant</em> Cases
          </h2>

          <p ref={descRef} className="cs-description">
            Explore our most significant legal victories and the strategies that
            led to successful outcomes for our clients. Each case study
            demonstrates our commitment to excellence in legal representation.
          </p>

          <div className="cs-heading-line" />
        </div>

        {/* ── Cards ── */}
        <div className="cs-grid">
          {caseStudies.map((cs, index) => (
            <article key={cs.id} className="cs-card">
              {/* Index label */}
              <div className="cs-card__index">
                {String(index + 1).padStart(2, "0")}
              </div>

              {/* Image */}
              <div className="cs-card__image-wrap">
                <img src={cs.image} alt={cs.title} className="cs-card__img" />
                <div className="cs-card__image-overlay" />
                <span className="cs-card__tag">{cs.tag}</span>
              </div>

              {/* Content */}
              <div className="cs-card__body">
                <h3 className="cs-card__title">{cs.title}</h3>

                <div className="cs-card__divider" />

                <p className="cs-card__desc">{cs.description}</p>

                <div className="cs-card__meta">
                  <div className="cs-meta-item">
                    <FaClock className="cs-meta-icon" />
                    <span>{cs.duration}</span>
                  </div>
                  <div className="cs-meta-item cs-meta-item--outcome">
                    <BiBookReader className="cs-meta-icon" />
                    <span>{cs.outcome}</span>
                  </div>
                </div>

                <button
                  className="cs-card__btn"
                  onClick={() => navigate(`/case-studies/${cs.id}`)}
                >
                  <span>View Full Case Study</span>
                  <span className="cs-card__btn-arrow">→</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Go />
    </>
  );
};

export default Case;
